class FloatingStatusDisplay {
  constructor(debug = false) {
    this.container = null;
    this.visible = false;
    this.debug = debug;

    // total stats for full conversation
    this.totalStats = this._initStats();

    // current stats for last response
    this.currentStats = this._initStats();

    // pricing per 1M tokens — gpt-realtime-mini
    // source: developers.openai.com/api/docs/pricing (checked 2026-10-22)
    this.pricing = {
      text:  { input: 0.60, cached: 0.06, output: 2.40  },
      audio: { input: 10.00, cached: 0.30, output: 20.00 },
      image: { input: 0.80, cached: 0.08, output: 0.00  },
    };

    this._createUI();
  }

  // ============================
  // Helpers
  // ============================

  _initStats() {
    return {
      input_text: 0,
      cached_text: 0,
      output_text: 0,
      input_audio: 0,
      cached_audio: 0,
      output_audio: 0,
      input_image: 0,
      cached_image: 0,
      output_image: 0,
      total: 0
    };
  }

  // ============================
  // UI
  // ============================

  _createUI() {
    this.container = document.createElement('div');
    this.container.style.cssText = `
      position: fixed;
      left: 50%;
      bottom: 24px;
      transform: translateX(-50%);
      padding: 12px 16px;
      background: rgba(0, 0, 0, 0.85);
      color: #fff;
      font-size: 13px;
      line-height: 1.4;
      border-radius: 12px;
      font-family: system-ui, -apple-system, sans-serif;
      opacity: 0;
      transition: opacity 0.25s ease;
      pointer-events: none;
      z-index: 9999;
      white-space: pre-line;
    `;
    document.body.appendChild(this.container);
  }

  show(text) {
    if (!this.debug) return;
    this.container.textContent = text;
    this.container.style.opacity = '1';
    this.visible = true;
  }

  hide() {
    this.container.style.opacity = '0';
    this.visible = false;
  }

  // ============================
  // TOKEN TRACKING
  // ============================

  addUsage({ input_token_details = {}, output_token_details = {} }) {
    // reset current stats
    this.currentStats = this._initStats();

    // input tokens
    this.currentStats.input_text = input_token_details.text_tokens || 0;
    this.currentStats.input_audio = input_token_details.audio_tokens || 0;
    this.currentStats.input_image = input_token_details.image_tokens || 0;

    // cached tokens
    this.currentStats.cached_text = input_token_details.cached_tokens_details?.text_tokens || 0;
    this.currentStats.cached_audio = input_token_details.cached_tokens_details?.audio_tokens || 0;
    this.currentStats.cached_image = input_token_details.cached_tokens_details?.image_tokens || 0;

    // output tokens
    this.currentStats.output_text = output_token_details.text_tokens || 0;
    this.currentStats.output_audio = output_token_details.audio_tokens || 0;
    this.currentStats.output_image = output_token_details.image_tokens || 0;

    // total tokens (raw)
    this.currentStats.total =
      this.currentStats.input_text +
      this.currentStats.output_text +
      this.currentStats.input_audio +
      this.currentStats.output_audio +
      this.currentStats.input_image +
      this.currentStats.output_image;

    // accumulate totals for conversation
    for (const key in this.currentStats) {
      this.totalStats[key] += this.currentStats[key];
    }
  }

  resetUsage() {
    this.currentStats = this._initStats();
    this.totalStats = this._initStats();
  }

  // ============================
  // COST CALCULATION
  // ============================

  calculateCost(stats) {
    const { text, audio, image } = this.pricing;

    const costText =
      (Math.max(0, stats.input_text  - stats.cached_text)  / 1_000_000) * text.input  +
      (stats.cached_text   / 1_000_000) * text.cached +
      (stats.output_text   / 1_000_000) * text.output;

    const costAudio =
      (Math.max(0, stats.input_audio - stats.cached_audio) / 1_000_000) * audio.input  +
      (stats.cached_audio  / 1_000_000) * audio.cached +
      (stats.output_audio  / 1_000_000) * audio.output;

    const costImage =
      (Math.max(0, stats.input_image - stats.cached_image) / 1_000_000) * image.input  +
      (stats.cached_image  / 1_000_000) * image.cached;

    return {
      costText, costAudio, costImage,
      totalCost: costText + costAudio + costImage,
    };
  }

  // ============================
  // SUMMARY DISPLAY
  // ============================

  showSummary() {
    const curr = this.currentStats;
    const total = this.totalStats;

    const costCurr = this.calculateCost(curr);
    const costTotal = this.calculateCost(total);

    const summary = `
🎯 Current Response:
Text: ${curr.input_text - curr.cached_text} in, ${curr.cached_text} cached, ${curr.output_text} out — $${costCurr.costText.toFixed(4)}
Audio: ${curr.input_audio - curr.cached_audio} in, ${curr.cached_audio} cached, ${curr.output_audio} out — $${costCurr.costAudio.toFixed(4)}
Cost: $${costCurr.totalCost.toFixed(4)}

📊 Total Conversation:
Text: ${total.input_text + total.output_text} tok — $${costTotal.costText.toFixed(4)}
Audio: ${total.input_audio + total.output_audio} tok — $${costTotal.costAudio.toFixed(4)}
Total: $${costTotal.totalCost.toFixed(4)}
`;

    this.show(summary);
    setTimeout(() => this.hide(), 10000);
  }

  // ============================
  // CLEANUP
  // ============================

  destroy() {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
    this.visible = false;
    this.resetUsage();
  }
}

export default FloatingStatusDisplay;
